import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Heart, Bus, Accessibility, Clock, HelpCircle, Phone } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { SectionHead, CatIcon, Img, Prose, DemoBadge } from "@/components/common";
import ActivityCard, { ActivityRow } from "@/components/ActivityCard";
import NowLive from "@/components/NowLive";
import Countdown from "@/components/Countdown";
import VenueMap from "@/components/VenueMap";
import useTitle from "@/lib/useTitle";
import { dayOf, fmtDay } from "@/lib/time";
import { fileUrl } from "@/lib/api";

const cta = "inline-flex items-center gap-2 h-14 px-7 rounded-full font-bold text-base transition-transform hover:-translate-y-0.5";

function Hero() {
  const { data } = useSite();
  const ed = data.edition;
  const s = data.settings || {};
  return (
    <section className="relative overflow-hidden bg-white text-[var(--ink)] border-b" data-testid="home-hero">
      {s.hero_video && <video src={fileUrl(s.hero_video)} autoPlay muted loop playsInline className="absolute inset-0 h-full w-full object-cover opacity-30" aria-hidden />}
      <div className="relative lg:flex lg:items-center lg:min-h-[34rem]">
        <div className="relative h-60 sm:h-80 lg:absolute lg:inset-y-0 lg:right-0 lg:h-auto lg:w-[65%]" data-testid="hero-photo-wrap">
          <img src="/img/jardim-ajuda.jpg" alt="Jardim Botânico da Ajuda com jacarandás em flor" className="absolute inset-0 h-full w-full object-cover" data-testid="hero-photo" />
          <div className="absolute inset-0 hidden lg:block bg-gradient-to-r from-white via-white/50 to-transparent" aria-hidden data-testid="hero-photo-fade" />
        </div>
        <div className="relative z-10 lg:w-[55%] px-5 sm:px-8 lg:pl-16 xl:pl-28 lg:pr-12 pt-10 sm:pt-14 pb-14 sm:pb-16 lg:py-8">
          <p className="eyebrow text-slate-500 rise">Semana da Saúde Mental · {ed.motto}</p>
          <h1 className="mt-5 uppercase leading-[0.9] rise" style={{ animationDelay: ".08s" }}>
            <span className="block font-light text-5xl sm:text-7xl lg:text-6xl xl:text-7xl tracking-tight">Feira de</span>
            <span className="block font-extrabold text-5xl sm:text-7xl lg:text-6xl xl:text-7xl tracking-tight">Saúde e<br />Bem-Estar</span>
          </h1>
          <span className="block h-1.5 w-full max-w-md brand-stripe mt-6 rise" aria-hidden />
          <p className="mt-5 lg:mt-4 text-xl sm:text-3xl lg:text-2xl font-normal rise" style={{ animationDelay: ".16s" }}>Para uma vida mais saudável</p>
          <div className="mt-10 lg:mt-6 font-cond uppercase rise" style={{ animationDelay: ".24s" }}>
            <p className="text-4xl sm:text-6xl lg:text-5xl font-bold leading-none" data-testid="hero-date">{fmtDay(ed.fair_date, false).replace(/ de \d{4}$/, "")}</p>
            <p className="text-2xl sm:text-4xl lg:text-3xl font-medium text-[var(--leaf)] mt-1">Das 10h00 às 18h00</p>
            <p className="text-2xl sm:text-3xl lg:text-2xl font-bold text-[var(--clay)] mt-1" data-testid="hero-location">{ed.location.replace(", Lisboa", "")}</p>
          </div>
          <div className="mt-8 lg:mt-6 rise" style={{ animationDelay: ".28s" }}><Countdown date={ed.fair_date} /></div>
          <div className="mt-8 lg:mt-6 flex flex-col sm:flex-row gap-3 rise" style={{ animationDelay: ".32s" }}>
            <Link to="/programacao" className={`${cta} bg-[var(--ink)] text-white uppercase tracking-wide text-sm justify-center`} data-testid="hero-explore-programme-button">Explorar programação <ArrowRight className="h-5 w-5" aria-hidden /></Link>
            <Link to="/a-minha-feira" className={`${cta} border-2 border-[var(--ink)] text-[var(--ink)] uppercase tracking-wide text-sm justify-center hover:bg-slate-50`} data-testid="hero-my-fair-button"><Heart className="h-5 w-5" aria-hidden />A minha Feira</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function About() {
  const { data } = useSite();
  const page = data.pages.find((p) => p.slug === "a-feira");
  const facts = [["1 dia", "de atividades abertas a todas as pessoas"], [`${data.categories.length}`, "categorias de atividades"], ["Out–Dez", "Semana da Saúde Mental 2026"]];
  return (
    <section className="container-x py-20 lg:py-28 grid gap-12 lg:grid-cols-12" aria-labelledby="a-feira">
      <div className="lg:col-span-6">
        <p className="eyebrow text-[var(--clay)] mb-3">A Feira</p>
        <h2 id="a-feira" className="font-display text-4xl sm:text-5xl font-extrabold leading-[0.95]">A saúde mental faz-se <span className="text-[var(--leaf)]">em comunidade.</span></h2>
        <Prose text={page?.body} className="mt-8 text-base md:text-lg" />
        <dl className="mt-10 grid grid-cols-3 gap-4 border-t pt-8">
          {facts.map(([n, l]) => <div key={l}><dt className="font-display text-2xl sm:text-4xl font-black text-[var(--clay)] whitespace-nowrap">{n}</dt><dd className="text-sm text-slate-600 mt-1">{l}</dd></div>)}
        </dl>
      </div>
      <div className="lg:col-span-6 grid grid-cols-2 gap-4 self-center">
        <Img src="/img/act/real-protecao-civil.jpg" alt="Stand da Proteção Civil Lisboa na Feira" className="rounded-3xl aspect-[3/4] w-full object-cover" />
        <div className="grid gap-4 pt-12">
          <Img src="/img/act/real-cavalos-gnr.jpg" alt="Crianças junto aos cavalos da GNR" className="rounded-3xl aspect-square w-full object-cover" />
          <Img src="/img/act/real-bancas.jpg" alt="Banca de uma associação na Feira" className="rounded-3xl aspect-square w-full object-cover" />
        </div>
      </div>
    </section>
  );
}

function Highlights() {
  const { data } = useSite();
  const feat = data.activities.filter((a) => a.featured).slice(0, 6);
  if (!feat.length) return null;
  return (
    <section className="py-20 lg:py-24 bg-white border-t" aria-labelledby="destaques">
      <div className="container-x">
        <SectionHead id="destaques" eyebrow="Destaques" title="Não perca" action={<Link to="/programacao" className="font-semibold underline underline-offset-4" data-testid="highlights-see-all">Ver toda a programação</Link>} />
        <div className="flex lg:grid lg:grid-cols-3 gap-5 overflow-x-auto no-scrollbar snap-x -mx-5 px-5 lg:mx-0 lg:px-0">
          {feat.map((a) => <div key={a.id} className="snap-start shrink-0 w-[82%] sm:w-[46%] lg:w-auto"><ActivityCard act={a} /></div>)}
        </div>
      </div>
    </section>
  );
}

function NowSection() {
  const { data } = useSite();
  const changes = data.activities.filter((a) => a.status === "alterada" || a.status === "cancelada");
  return (
    <section className="bg-[var(--ink)] text-white py-20 lg:py-24 relative overflow-hidden stripes" aria-labelledby="agora">
      <div className="container-x relative">
        <p className="eyebrow text-[var(--sun)] mb-3 inline-flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-red-600 live-dot" />Em tempo real</p>
        <h2 id="agora" className="font-display text-4xl sm:text-5xl font-extrabold mb-8">Agora na Feira</h2>
        <NowLive />
        {changes.length > 0 && (
          <div className="mt-10 rounded-2xl bg-amber-400 text-[var(--ink)] p-5" data-testid="programme-changes">
            <p className="font-display text-lg font-bold">Alterações de programação</p>
            <ul className="mt-2 space-y-1 text-sm">{changes.map((a) => <li key={a.id}><Link className="underline font-semibold" to={`/atividade/${a.slug}`}>{a.title}</Link> — {a.status === "cancelada" ? "cancelada" : "alterada"}{a.change_note ? `: ${a.change_note}` : ""}</li>)}</ul>
          </div>
        )}
      </div>
    </section>
  );
}

function DayAgenda() {
  const { data } = useSite();
  const day = data.edition.fair_date;
  const acts = data.activities.filter((a) => dayOf(a.start) === day);
  return (
    <section className="container-x py-20 lg:py-24" aria-labelledby="agenda">
      <SectionHead id="agenda" eyebrow={fmtDay(day)} title="Agenda do dia">Do primeiro passeio da manhã ao último acorde do fim de tarde.</SectionHead>
      <div className="grid gap-3">{acts.slice(0, 8).map((a) => <ActivityRow key={a.id} act={a} />)}</div>
      <Link to="/programacao" className="mt-8 inline-flex items-center gap-2 font-bold text-[var(--leaf)]" data-testid="agenda-see-all">Ver as {acts.length} atividades <ArrowRight className="h-4 w-4" aria-hidden /></Link>
    </section>
  );
}

function Categories() {
  const { data } = useSite();
  return (
    <section className="container-x pb-20 lg:pb-24" aria-labelledby="categorias">
      <SectionHead id="categorias" eyebrow="Explorar por tema" title="Categorias" />
      <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {data.categories.map((c) => (
          <li key={c.id}>
            <Link to={`/programacao?categoria=${c.slug}`} data-testid={`category-tile-${c.slug}`}
              className="group flex flex-col justify-between h-36 sm:h-40 rounded-3xl p-5 text-white transition-transform hover:-translate-y-1" style={{ background: c.color }}>
              <CatIcon name={c.icon} className="h-7 w-7 group-hover:scale-110 transition-transform" />
              <span className="font-display text-lg sm:text-xl font-bold leading-tight">{c.name}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function MapSection() {
  return (
    <section className="bg-white border-t py-20 lg:py-24" aria-labelledby="mapa">
      <div className="container-x">
        <SectionHead id="mapa" eyebrow="Jardim Botânico da Ajuda" title="Mapa do recinto" action={<Link to="/mapa" className="font-semibold underline underline-offset-4" data-testid="home-map-full-link">Abrir mapa completo</Link>} />
        <VenueMap compact />
      </div>
    </section>
  );
}

function Orgs() {
  const { data } = useSite();
  return (
    <section className="container-x py-20 lg:py-24" aria-labelledby="orgs">
      <SectionHead id="orgs" eyebrow="Quem participa" title="Organizações e parceiros" action={<Link to="/organizacoes" className="font-semibold underline underline-offset-4" data-testid="home-orgs-link">Diretório completo</Link>} />
      <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {data.organizations.slice(0, 12).map((o) => (
          <li key={o.id}><Link to={`/organizacoes/${o.slug}`} className="flex flex-col items-start gap-3 h-full rounded-2xl border bg-white p-4 hover:border-black/30 transition-colors" data-testid={`home-org-${o.slug}`}>
            {o.logo ? <img src={fileUrl(o.logo)} alt={`Logótipo ${o.name}`} className="h-12 object-contain" /> : <span className="h-12 w-12 rounded-xl bg-[var(--leaf)] text-white grid place-items-center font-display font-black text-lg">{o.name.replace(/^(Associação|Coletivo|Rede|Projeto|Grupo|Oficina) Exemplo /, "").slice(0, 2)}</span>}
            <span className="text-sm font-semibold leading-snug">{o.name}</span><DemoBadge show={o.demo} />
          </Link></li>
        ))}
      </ul>
      {(data.settings?.support_banner || data.settings?.partners_banner) && (
        <div className="mt-12 grid gap-4" data-testid="partners-banners">
          {[data.settings.support_banner, data.settings.partners_banner].filter(Boolean).map((src) => (
            <img key={src} src={fileUrl(src)} alt={`Logótipos: ${data.partners.map((p) => p.name).join(", ")}`} className="w-full rounded-2xl bg-white border p-3 sm:p-6" loading="lazy" />
          ))}
        </div>
      )}
      {data.partners.length > 0 && (
        <details className="mt-6 border-t pt-6"><summary className="cursor-pointer font-semibold min-h-[44px]" data-testid="partners-list-toggle">Ver lista de entidades</summary><div className="mt-6 grid gap-8">
          {[...new Set(data.partners.map((p) => p.tier))].map((tier) => (
            <div key={tier} data-testid={`partners-tier-${tier}`}>
              <p className="eyebrow text-[var(--clay)] mb-3">{tier}</p>
              <ul className="flex flex-wrap gap-2">
                {data.partners.filter((p) => p.tier === tier).map((p) => (
                  <li key={p.id} data-testid={`partner-${p.slug}`} className="inline-flex items-center gap-2 rounded-full border bg-white px-4 py-2 text-sm font-semibold">{p.logo && <img src={fileUrl(p.logo)} alt="" className="h-6" />}{p.url ? <a href={p.url} target="_blank" rel="noreferrer" className="hover:underline">{p.name}</a> : p.name}</li>
                ))}
              </ul>
            </div>
          ))}
        </div></details>
      )}
    </section>
  );
}

function News() {
  const { data } = useSite();
  if (!data.news.length) return null;
  const [first, ...rest] = data.news;
  return (
    <section className="bg-white border-t py-20 lg:py-24" aria-labelledby="noticias">
      <div className="container-x">
        <SectionHead id="noticias" eyebrow="Novidades" title="Notícias" action={<Link to="/noticias" className="font-semibold underline underline-offset-4" data-testid="home-news-link">Todas as notícias</Link>} />
        <div className="grid gap-6 lg:grid-cols-12">
          <Link to={`/noticias/${first.slug}`} className="lg:col-span-7 group" data-testid={`home-news-${first.slug}`}>
            <Img src={first.image} alt="" className="rounded-3xl aspect-[16/10] w-full object-cover" />
            <p className="eyebrow text-slate-500 mt-4">{fmtDay(first.date, false)}</p>
            <h3 className="font-display text-2xl sm:text-3xl font-bold mt-1 group-hover:text-[var(--leaf)]">{first.title}</h3>
          </Link>
          <div className="lg:col-span-5 grid gap-6 content-start">
            {rest.slice(0, 3).map((n) => (
              <Link key={n.id} to={`/noticias/${n.slug}`} className="grid grid-cols-[120px_1fr] gap-4 group" data-testid={`home-news-${n.slug}`}>
                <Img src={n.image} alt="" className="rounded-2xl aspect-square w-full object-cover" />
                <div><p className="eyebrow text-slate-500">{fmtDay(n.date, false)}</p><h3 className="font-display text-lg font-bold mt-1 group-hover:text-[var(--leaf)]">{n.title}</h3></div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Practical() {
  const items = [["como-chegar", "Como chegar", MapPin], ["transportes", "Transportes públicos", Bus], ["acessibilidade", "Acessibilidade", Accessibility], ["horarios", "Horários", Clock], ["perguntas", "Perguntas frequentes", HelpCircle], ["contactos", "Contactos", Phone]];
  return (
    <section className="bg-[var(--leaf)] text-white py-20 lg:py-24 relative overflow-hidden stripes" aria-labelledby="info">
      <div className="container-x relative">
        <p className="eyebrow text-[var(--sun)] mb-3">Planear a visita</p>
        <h2 id="info" className="font-display text-4xl sm:text-5xl font-extrabold mb-10">Informações práticas</h2>
        <ul className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          {items.map(([slug, label, Icon]) => (
            <li key={slug}><Link to={`/informacoes#${slug}`} className="flex flex-col gap-6 rounded-2xl bg-white/10 hover:bg-white/20 p-5 h-full transition-colors" data-testid={`practical-${slug}`}><Icon className="h-7 w-7 text-[var(--sun)]" aria-hidden /><span className="font-display text-lg font-bold">{label}</span></Link></li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export default function Home() {
  useTitle("");
  const { data } = useSite();
  return (
    <>
      {data.settings?.announcement && <div className="bg-[var(--sun)] text-[var(--ink)] text-center text-sm font-semibold py-2 px-4" data-testid="announcement-bar">{data.settings.announcement}</div>}
      <Hero />
      <About />
      <Highlights />
      <NowSection />
      <DayAgenda />
      <Categories />
      <MapSection />
      <Orgs />
      <News />
      <Practical />
    </>
  );
}
